import type { PaginationDto, PaginationResponseDto } from "core/dto";
import { ORDER_BY } from "core/interfaces";
import type { PageMetaDto } from "shared/dto";

import { getPaginationDefault } from "~/common/defaults/pagination.default";
import { getPaginationResponseDefault } from "~/common/defaults/pagination-response.default";
import { getSizeDefault } from "~/common/defaults/size.default";
import type { IdDto } from "~/common/interfaces/dto/id.dto";
import type { CreateSizeDto } from "~/common/interfaces/dto/sizes/create-size.dto";
import type { GetAllSizesDto } from "~/common/interfaces/dto/sizes/get-all-sizes.dto";
import type { UpdateSizeByIdDto } from "~/common/interfaces/dto/sizes/update-size-by-id.dto";
import type { Size } from "~/common/interfaces/entities/size.interface";
import type { Sort } from "~/common/interfaces/sort.interface";

export const useSizesStore = defineStore(
	"sizes",
	() => {
		const currentSize = ref<Size>(getSizeDefault());
		const sizes = ref<Size[]>([]);
		const meta = ref<PageMetaDto>(getPaginationResponseDefault());
		const recentGetAllQuery = ref<GetAllSizesDto & PaginationDto>();
		const tableSort = ref<Sort>({
			columns: ["width", "height", "length"],
			orderBy: ["width", ORDER_BY.ASC]
		});

		const createOne = async (dto: CreateSizeDto): Promise<Size> => {
			const size = await useNuxtApp().$apiFetch<Size>("/sizes", {
				method: "POST",
				body: dto
			});

			return size;
		};
		const getAll = async (
			dto: GetAllSizesDto = {},
			paginationDto?: Partial<PaginationDto>
		): Promise<PaginationResponseDto<Size>> => {
			const [column, orderBy] = tableSort.value.orderBy;

			recentGetAllQuery.value = {
				...getPaginationDefault(),
				...paginationDto,
				...dto,
				column,
				orderBy
			};

			const res = await useNuxtApp().$apiFetch<PaginationResponseDto<Size>>("/sizes", {
				query: recentGetAllQuery.value
			});
			sizes.value = res.data;
			meta.value = res.meta;

			return res;
		};
		const getById = (id: number): Promise<Size> => {
			return useNuxtApp().$apiFetch<Size>(`/sizes/${id}`);
		};
		const patchById = (id: number, dto: UpdateSizeByIdDto): Promise<Size> => {
			return useNuxtApp().$apiFetch<Size>(`/sizes/${id}`, {
				method: "PATCH",
				body: dto
			});
		};
		const deleteById = async (id: number): Promise<IdDto> => {
			const res = await useNuxtApp().$apiFetch<IdDto>(`/sizes/${id}`, {
				method: "DELETE"
			});

			return res;
		};

		return {
			currentSize,
			sizes,
			meta,
			recentGetAllQuery,
			tableSort,

			createOne,
			getAll,
			getById,
			patchById,
			deleteById
		};
	},
	{
		persist: {
			paths: ["tableSort.orderBy"]
		}
	}
);
